import type { ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { groupByCategory, sortLines } from './batch-utils';
import type { BatchSortKey } from './batch-utils';
import { lineCategory, lineStatusLabel, lineTone, TONE_BADGE, TONE_TEXT } from './batch-line-utils';
import type { BatchLine } from './batch-line-utils';

export type { BatchLine, BatchLineOutcome, OutcomeTone } from './batch-line-utils';

interface BatchLineListProps {
  lines: BatchLine[];
  sortKey: BatchSortKey;
  onSortKeyChange: (key: BatchSortKey) => void;
  groupByCategoryEnabled: boolean;
  onGroupByCategoryChange: (enabled: boolean) => void;
  onRemove?: (line: BatchLine) => void;
  /** Extra controls per line (e.g. pass/fail toggle). */
  renderLineExtra?: (line: BatchLine) => ReactNode;
  disabled?: boolean;
  emptyText?: string;
  maxHeightClass?: string;
}

/**
 * The asset list for a batch, grouped by category (carabiners, descenders,
 * ropes…) and sortable. Every line shows the matched asset or why it can't go.
 */
export function BatchLineList({
  lines,
  sortKey,
  onSortKeyChange,
  groupByCategoryEnabled,
  onGroupByCategoryChange,
  onRemove,
  renderLineExtra,
  disabled = false,
  emptyText = 'Scanned or pasted serials will appear here.',
  maxHeightClass = 'max-h-[36rem]',
}: BatchLineListProps) {
  const sorted = sortLines(lines, sortKey, {
    category: (line) => lineCategory(line)?.label ?? '￿',
    serial: (line) => line.serial,
    status: (line) => lineStatusLabel(line),
  });
  const groups = groupByCategoryEnabled
    ? groupByCategory(sorted, lineCategory)
    : [{ key: 'all', label: 'All items', items: sorted }];

  const renderLine = (line: BatchLine) => {
    const tone = lineTone(line);
    const asset = line.resolved?.asset;
    const message = line.outcome?.message ?? line.resolved?.message ?? 'Looking up in the register…';
    return (
      <div key={line.id} className="p-3 flex items-start justify-between gap-3" data-testid="batch-line">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-mono text-sm">{line.serial}</p>
            <Badge variant="outline" className={`text-[10px] ${TONE_BADGE[tone]}`}>
              {lineStatusLabel(line)}
            </Badge>
            {asset && asset.status !== 'available' && asset.status !== 'active' && (
              <Badge variant="outline" className="text-[10px]">{asset.status_display}</Badge>
            )}
          </div>
          {asset && (
            <p className="text-xs text-gray-600 truncate">
              {asset.display_name} · {asset.type_display}
              {asset.next_inspection_due ? ` · next inspection ${asset.next_inspection_due}` : ''}
            </p>
          )}
          <p className={`text-xs ${TONE_TEXT[tone]}`}>{message}</p>
          {!line.outcome && line.resolved?.warnings.map((warning) => (
            <p key={warning} className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              {warning}
            </p>
          ))}
          {renderLineExtra?.(line)}
        </div>
        {onRemove && !line.outcome && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label={`Remove ${line.serial}`}
            disabled={disabled}
            onClick={() => onRemove(line)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="batch-sort" className="text-xs">Sort by</Label>
          <Select value={sortKey} onValueChange={(value) => onSortKeyChange(value as BatchSortKey)}>
            <SelectTrigger id="batch-sort" className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="entered">Entry order</SelectItem>
              <SelectItem value="category">Category</SelectItem>
              <SelectItem value="serial">Serial</SelectItem>
              <SelectItem value="status">Status</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-1">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={groupByCategoryEnabled}
            onChange={(event) => onGroupByCategoryChange(event.target.checked)}
          />
          Group by category
        </label>
      </div>

      {lines.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyText}</p>
      ) : (
        <div className={`${maxHeightClass} overflow-y-auto rounded-lg border bg-white divide-y`}>
          {groups.map((group) => (
            <div key={group.key}>
              {groupByCategoryEnabled && (
                <div className="sticky top-0 z-10 flex items-center justify-between bg-gray-50 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 border-b">
                  <span>{group.label}</span>
                  <span>{group.items.length}</span>
                </div>
              )}
              <div className="divide-y">{group.items.map(renderLine)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
